import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
// --- Interfaces ---

export interface Contract {
  id: number;
  mainContract: boolean;
  startDate: string;
  endDate: string | null;
  earliestEndDate: string | null;
  /**
   * Set when a cancelled contract was reactivated. Old CMT anchors the
   * "contract younger than two months" check on this date when present
   * (contractRevisions.js:1047). Declared optional because AMIE constructs Contract
   * objects in tests and fixtures; the API itself always sends the key.
   */
  reactivatedStartDate?: string | null;
  terminable: boolean;
  deactivated: boolean;
  editable: boolean;
  /** Backend flag: whether the contract's start date may be moved (main-contract only, see Move Start Date action). */
  canChangeStartDate: boolean;
  /**
   * Plan of the contract's current/last revision. The contract-list endpoint
   * returns this (incl. `level`), so the trial badge can be derived from the
   * DTO directly — no need to wait for the lazily-loaded revisions (EB-3922).
   */
  plan?: { level: number } | null;
}

/** Mapped revision used by UI components */
export interface ContractRevision {
  startDate: string;
  endDate: string | null;
  productName: string;
  productCount: number;
  /** List price (monthly fee before discount); the discounted price is `RevisionListComponent.planPrice()`. */
  price: number;
  discount: number;
  priceListName: string;
  priceListId: number;
  /** Plan of this revision — the Add-Extension catalog is plan-scoped (old CMT parity, EB-4209). */
  planId: number | null;
  tariff: string;
  bookedExtensions: BookedExtension[];
  bookedFeatures: BookedFeature[];
  creatingEmployeeID: number | null;
  creatingEmployeeName?: string;
  creationDate: string;
  changedBy: number | null;
  changedByName?: string;
  changedOn: string;
  /** CMB-2953: why this revision was created and/or a (permanent) discount was granted. */
  reason: string | null;
}

export interface BookedExtension {
  name: string;
  extensionPriceInfo: { name: string; labelKey: string; monthlyFee: number };
  count: number;
  /** Price after discount (a negative discount is a surcharge); the list price is `extensionPriceInfo.monthlyFee`. */
  effectivePrice: number;
  discount: number;
  startDate: string;
  endDate: string | null;
}

export interface BookedFeature {
  name: string;
  discount: number;
  unitPrice: number;
  contingent: number | null;
  activeDays: string;
  activeFrom: string | null;
  activeUntil: string | null;
}

/** Raw API revision shape */
interface ApiRevision {
  startDate: string;
  endDate: string | null;
  product: { name: string; comment: string | null };
  productLabelKey: string;
  productCount: number;
  monthlyFee: number;
  discount: number;
  plan: { id: number; name: string; level: number; priceList: { id: number; name: string } } | null;
  priceListId: number;
  tariff: string;
  bookedExtensions: ApiBookedExtension[];
  bookedFeatures: ApiBookedFeature[];
  creatingEmployeeID: number | null;
  creationDate: string;
  changedBy: number | null;
  changedOn: string;
  reason: string | null;
}

interface ApiBookedExtension {
  extension: { name: string; comment: string | null };
  extensionLabelKey: string;
  count: number;
  monthlyFee: number;
  discount: number;
  product: string;
}

interface ApiBookedFeature {
  name: string;
  discount: number;
  unitPrice: number;
  contingent: number | null;
  activeDays: string;
  activeFrom: string | null;
  activeUntil: string | null;
}

/** Available plan returned by the plan change API */
export interface AvailablePlan {
  id: number;
  name: string;
  monthlyFee: number;
  level: number;
  priceList: { id: number; name: string };
}

/** Booked extension on a specific date */
export interface BookedExtensionEntry {
  changeDate: string;
  startDate: string;
  /**
   * The last day the extension is active (**inclusive**) — which is why the Remove dialog uses
   * it as the date field's `max` unchanged and converts on the way out: `bookingmgmt` names the
   * value it writes `fromDate` (first *inactive* day), so `submit()` sends `lastDay + 1`. Old
   * CMT makes the same pair of moves (`contractRevisions.js:575-581` and `:209-210`).
   * Not yet confirmed against a live GET — see the PR #40 review thread on `maxLastDayStr`;
   * contract 14257 on tsrv carries a one-day TarifErw2 window as the fixture for that check.
   */
  endDate: string | null;
  changingEmployee: number | null;
  count: number;
  discount: number;
  extensionPriceInfo: {
    name: string;
    comment: string | null;
    labelKey: string;
    monthlyFee: number;
    status: string | null;
  };
  product: string;
}

/** Extension catalog entry from price list + tariff */
export interface ExtensionCatalogEntry {
  extensionPriceInfo: {
    name: string;
    comment: string | null;
    labelKey: string;
    monthlyFee: number;
    status: string | null;
  };
  multipleBookable: boolean;
  customPrice: boolean;
  bookableToAllContracts: boolean;
}

/** Request body for adding an extension */
export interface AddExtensionRequest {
  name: string;
  count: number;
  discount: number;
  startDate: string;
  bookToAllContracts: boolean;
  employeeId: number;
}

/** JSON Patch operation for contract/extension modification */
export interface PatchOperation {
  type: string;
  attribute: string;
  value?: string | number;
}

/** Request body for cancelling a customer */
export interface CancelCustomerRequest {
  prevention?: string;
  // Backend expects the full reason objects (not ids) — see bookingmgmt cancelCustomer schema
  // and old-CMT customerService.cancelCustomer (EB-3881).
  mainReason: CancellationReason;
  subReason?: CancellationReason;
  satisfaction?: number;
  employeeID: number;
}

/** Cancellation reason */
export interface CancellationReason {
  id: number;
  /** Stable machine code (e.g. "tooExpensive") — part of the object the cancel endpoint expects. */
  code: string;
  text: string;
  canceledBy: number;
}

// --- Service ---

@Injectable({ providedIn: 'root' })
export class ContractsService {
  private readonly http = inject(HttpClient);

  /** Cache for employee name lookups */
  private employeeCache = new Map<number, string>();

  /** Cache for feature display names (`languageId:brandId` -> map) */
  private featureNamesCache = new Map<string, Record<string, string>>();

  /** Cache for extension display names (`languageId:brandId` -> map, keyed by extension labelKey) */
  private extensionNamesCache = new Map<string, Record<string, string>>();

  /**
   * Cache for booked extensions, keyed `contractId:validFrom`. The extensions endpoint is the
   * slowest contract call (~1.5s), so the contract panel pre-warms this on expand and the
   * Change-Discount (extension) dialog reuses it → instant open. Invalidated on any contract
   * mutation (see `invalidateBookedExtensions`).
   */
  private bookedExtensionsCache = new Map<string, Observable<BookedExtensionEntry[]>>();

  /**
   * Cache for the extension catalog, keyed `priceListId:tariff`. Static reference data (price
   * list + tariff), shared across all contracts on the same price list — no invalidation needed.
   * Pre-warmed on expand so the Add-Extension dialog opens instantly.
   */
  private extensionCatalogCache = new Map<string, Observable<ExtensionCatalogEntry[]>>();

  /** Cache for the plan-scoped extension list (Add Extension), keyed by plan id. */
  private planExtensionsCache = new Map<number, Observable<ExtensionCatalogEntry[]>>();

  /** Resolve employee ID to name via user account API */
  getEmployeeName(employeeId: number): Observable<string> {
    const cached = this.employeeCache.get(employeeId);
    if (cached) return new Observable((sub) => { sub.next(cached); sub.complete(); });

    return this.http
      .get<{ firstName: string; lastName: string }>(`${environment.userAccountUrl}/useraccounts/${employeeId}`)
      .pipe(
        map((ua) => {
          const name = [ua.firstName, ua.lastName].filter(Boolean).join(' ') || `Employee #${employeeId}`;
          this.employeeCache.set(employeeId, name);
          return name;
        }),
      );
  }

  /** Get feature display names from localization API (language is a numeric ID, e.g. 1=DE) */
  getFeatureNames(languageId: number, brandId: number): Observable<Record<string, string>> {
    // Cache key carries the language too — see getExtensionNames.
    const cacheKey = `${languageId}:${brandId}`;
    const cached = this.featureNamesCache.get(cacheKey);
    if (cached) return new Observable((sub) => { sub.next(cached); sub.complete(); });

    return this.http
      .get<Record<string, string>>(
        `${environment.localizationV1Url}/i18n`,
        { params: { language: languageId.toString(), brandId: brandId.toString(), globalKey: 'featureShowname', category: 'defTarif' } },
      )
      .pipe(
        map((names) => {
          this.featureNamesCache.set(cacheKey, names);
          return names;
        }),
      );
  }

  /**
   * Extension display names from the localization API, keyed by extension
   * labelKey (category `pricePackageName`). Old CMT resolves each booked/catalog
   * extension's shown name as `map[labelKey]` and falls back to the raw labelKey
   * on a miss - never to the technical name (see contractRevisions.js
   * setExtensionsNames, and `extension-name.util.ts`). Cached per language+brand.
   */
  getExtensionNames(languageId: number, brandId: number): Observable<Record<string, string>> {
    // Cache key carries the language too — the map differs per customer language,
    // so a brand-only key would serve the first requested language to everyone.
    const cacheKey = `${languageId}:${brandId}`;
    const cached = this.extensionNamesCache.get(cacheKey);
    if (cached) return new Observable((sub) => { sub.next(cached); sub.complete(); });

    return this.http
      .get<Record<string, string>>(
        `${environment.localizationV1Url}/i18n`,
        { params: { language: languageId.toString(), brandId: brandId.toString(), category: 'pricePackageName' } },
      )
      .pipe(
        map((names) => {
          this.extensionNamesCache.set(cacheKey, names);
          return names;
        }),
      );
  }

  /** List all contracts for a customer */
  getContracts(customerId: number): Observable<Contract[]> {
    return this.http.get<Contract[]>(`${environment.contractUrl}/customers/${customerId}/contracts`);
  }

  /** Get revisions for a contract — maps raw API shape to UI model */
  getRevisions(contractId: number): Observable<ContractRevision[]> {
    return this.http
      .get<ApiRevision[]>(`${environment.contractUrl}/contracts/${contractId}`)
      .pipe(map((revisions) => revisions.map((r) => this.mapRevision(r))));
  }

  /**
   * The single revision valid on `validOn` (old CMT `getContractRevisionsValidOn`), or
   * `null` when the contract is not active on that date. Extension dialogs derive the
   * price list + tariff from it, so the offered catalog follows the selected date
   * (EB-4209 / EB-4210).
   */
  getRevisionValidOn(contractId: number, validOn: string): Observable<ContractRevision | null> {
    return this.http
      .get<ApiRevision[]>(`${environment.contractUrl}/contracts/${contractId}`, {
        params: { validOn },
      })
      .pipe(
        map((revisions) => {
          const revision = revisions.length ? this.mapRevision(revisions[0]) : null;
          // A revision without price list or tariff is not usable — old CMT treats it as
          // "unknown contract revision" and offers nothing rather than requesting
          // /pricelists/undefined/... (`contractRevisions.js:686`).
          return revision?.priceListId && revision.tariff ? revision : null;
        }),
      );
  }

  private mapRevision(r: ApiRevision): ContractRevision {
    return {
      startDate: r.startDate,
      endDate: r.endDate,
      productName: r.plan?.name || r.productLabelKey || r.product?.name || '',
      productCount: r.productCount,
      price: r.monthlyFee,
      discount: r.discount,
      priceListName: r.plan?.priceList?.name || '',
      priceListId: r.plan?.priceList?.id ?? r.priceListId,
      planId: r.plan?.id ?? null,
      tariff: r.tariff,
      bookedExtensions: (r.bookedExtensions ?? []).map((e) => ({
        name: e.extension?.name || '',
        extensionPriceInfo: {
          name: e.extension?.name || '',
          // No `|| e.extension?.name` here: the technical name is a different domain object
          // from the price package this field carries, and substituting it made the two
          // indistinguishable downstream. The revision list shows the technical name in its
          // own right now (PR #42 review #8).
          labelKey: e.extensionLabelKey || '',
          monthlyFee: e.monthlyFee,
        },
        count: e.count,
        // Effective (discounted) price as old CMT `setExtensionPrices` computes it; the list price stays in
        // extensionPriceInfo.monthlyFee. A negative discount is a surcharge (price above list); a missing
        // discount counts as none (old CMT would yield NaN).
        effectivePrice: e.monthlyFee * (1 - (e.discount ?? 0)),
        discount: e.discount,
        startDate: r.startDate,
        endDate: r.endDate,
      })),
      bookedFeatures: (r.bookedFeatures ?? []).map((f) => ({
        name: f.name,
        discount: f.discount,
        unitPrice: f.unitPrice,
        contingent: f.contingent,
        activeDays: f.activeDays,
        activeFrom: f.activeFrom,
        activeUntil: f.activeUntil,
      })).sort((a, b) => a.name.localeCompare(b.name)),
      creatingEmployeeID: r.creatingEmployeeID,
      creationDate: r.creationDate,
      changedBy: r.changedBy,
      changedOn: r.changedOn,
      reason: r.reason ?? null,
    };
  }

  /** Available plans for plan change */
  getAvailablePlans(contractId: number): Observable<AvailablePlan[]> {
    return this.http.get<AvailablePlan[]>(`${environment.contractUrl}/contracts/${contractId}/availablePlans`);
  }

  /** Booked extensions on a specific date (cached per `contractId:validFrom`, see field doc). */
  getBookedExtensions(contractId: number, validFrom: string): Observable<BookedExtensionEntry[]> {
    const key = `${contractId}:${validFrom}`;
    let cached = this.bookedExtensionsCache.get(key);
    if (!cached) {
      cached = this.http
        .get<BookedExtensionEntry[]>(`${environment.contractUrl}/contracts/${contractId}/extensions`, {
          params: { validFrom },
        })
        .pipe(
          // Don't let a transient failure stick in the cache — drop the key so a retry re-fetches.
          catchError((err) => {
            this.bookedExtensionsCache.delete(key);
            return throwError(() => err);
          }),
          shareReplay(1),
        );
      this.bookedExtensionsCache.set(key, cached);
    }
    return cached;
  }

  /**
   * Extensions bookable on a plan, including system extensions — old CMT's source for the
   * **Add Extension** list (`addingExtension.js:119` → `contract.revisions.service.js:53-55`).
   * Deliberately a different set from `getExtensionCatalog`, which old CMT uses only as the
   * system-extension *gate* for the Remove list. Cached per plan; static reference data.
   */
  getExtensionsByPlan(planId: number): Observable<ExtensionCatalogEntry[]> {
    const cached = this.planExtensionsCache.get(planId);
    if (cached) return cached;
    const request = this.http
      .get<ExtensionCatalogEntry[]>(`${environment.contractUrl}/plans/${planId}/extensions`, {
        params: { system: 'true' },
      })
      .pipe(
        catchError((err) => {
          this.planExtensionsCache.delete(planId);
          return throwError(() => err);
        }),
        shareReplay(1),
      );
    this.planExtensionsCache.set(planId, request);
    return request;
  }

  /** Extension catalog for a price list + tariff (cached per `priceListId:tariff`, see field doc). */
  getExtensionCatalog(priceListId: number, tariff: string): Observable<ExtensionCatalogEntry[]> {
    const key = `${priceListId}:${tariff}`;
    let cached = this.extensionCatalogCache.get(key);
    if (!cached) {
      cached = this.http
        .get<ExtensionCatalogEntry[]>(
          `${environment.contractUrl}/pricelists/${priceListId}/tariffs/${tariff}/extensions`,
          { params: { onlyShowableOnFe: 'true' } },
        )
        .pipe(
          catchError((err) => {
            this.extensionCatalogCache.delete(key);
            return throwError(() => err);
          }),
          shareReplay(1),
        );
      this.extensionCatalogCache.set(key, cached);
    }
    return cached;
  }

  /** Drop cached booked extensions for a contract after a mutation that may change them. */
  private invalidateBookedExtensions(contractId: number): void {
    const prefix = `${contractId}:`;
    for (const key of this.bookedExtensionsCache.keys()) {
      if (key.startsWith(prefix)) this.bookedExtensionsCache.delete(key);
    }
  }

  /**
   * A booking that hits every contract of the customer cannot be mapped to contract ids here,
   * so the whole cache goes — same reasoning as `syncExtensions`. Without this, a sibling
   * contract already expanded in the panel keeps offering an extension that was just booked
   * customer-wide, or listing one that was just removed (EB-4209 review run 2).
   */
  private invalidateBookedExtensionsForCustomer(): void {
    this.bookedExtensionsCache.clear();
  }

  /** Add extension to a contract */
  addExtension(contractId: number, data: AddExtensionRequest): Observable<void> {
    return this.http
      .post<void>(`${environment.bookingMgmtUrl}/extensions`, data, {
        params: { contractId: contractId.toString() },
      })
      .pipe(tap(() => (data.bookToAllContracts
        ? this.invalidateBookedExtensionsForCustomer()
        : this.invalidateBookedExtensions(contractId))));
  }

  /**
   * Remove extension from a contract. `justFromThisContract` decides whether the removal hits
   * this contract alone or the whole customer; it is only *offered* for `TarifErw2` (the 24h
   * extension) but always sent, exactly as old CMT does — its `deleteFromThisContract` is
   * initialised to `false`, so the parameter goes out on every removal
   * (`booking.management.service.js:30-37`, `contractRevisions.js:49`). EB-4209.
   */
  removeExtension(
    extensionName: string,
    contractId: number,
    endDate: string,
    employeeId: string,
    justFromThisContract: boolean,
  ): Observable<void> {
    const params: Record<string, string> = {
      contractId: contractId.toString(),
      endDate,
      employeeId,
      justFromThisContract: String(justFromThisContract),
    };
    return this.http
      .delete<void>(`${environment.bookingMgmtUrl}/extensions/${extensionName}`, { params })
      .pipe(tap(() => (justFromThisContract
        ? this.invalidateBookedExtensions(contractId)
        : this.invalidateBookedExtensionsForCustomer())));
  }

  /** Modify contract (plan, discount, endDate, startDate) via JSON Patch */
  modifyContract(
    contractId: number,
    body: PatchOperation[],
    params: Record<string, string>,
  ): Observable<void> {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      httpParams = httpParams.set(key, value);
    }
    return this.http
      .patch<void>(`${environment.bookingMgmtUrl}/contracts/${contractId}`, body, {
        params: httpParams,
      })
      .pipe(tap(() => this.invalidateBookedExtensions(contractId)));
  }

  /**
   * Modify a contract directly against the contract API (not bookingmgmt) so the `reason` query
   * param survives — bookingmgmt is a pass-through that does not forward it. Used for permanent
   * (no-endDate) discounts (CMB-2953), which require a reason and the permission gate on the server.
   */
  modifyContractDirect(
    contractId: number,
    body: PatchOperation[],
    params: Record<string, string>,
  ): Observable<void> {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      httpParams = httpParams.set(key, value);
    }
    return this.http
      .patch<void>(`${environment.contractUrl}/contracts/${contractId}`, body, {
        params: httpParams,
      })
      .pipe(tap(() => this.invalidateBookedExtensions(contractId)));
  }

  /** Modify extension discount */
  modifyExtensionDiscount(
    contractId: number,
    extensionName: string,
    body: PatchOperation[],
    params: Record<string, string>,
  ): Observable<void> {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      httpParams = httpParams.set(key, value);
    }
    return this.http
      .patch<void>(
        `${environment.contractUrl}/contracts/${contractId}/extensions/${extensionName}`,
        body,
        { params: httpParams },
      )
      .pipe(tap(() => this.invalidateBookedExtensions(contractId)));
  }

  /**
   * Extend a free trial by a number of days. Reuses the contract PATCH endpoint with a
   * `trial` operation, called directly via `environment.contractUrl` (NOT bookingmgmt —
   * trial extension is trial-only and does not cascade to other services; precedent:
   * `modifyExtensionDiscount`).
   *
   * `PATCH {contractUrl}/contracts/{contractId}?employeeId=N` with a `trial` patch
   * operation whose value is the number of days to extend by.
   * Backend guards: 400 on guard violation, 409 when the 3x cap is reached.
   */
  extendFreeTrial(contractId: number, days: number, employeeId: number): Observable<void> {
    const params = new HttpParams().set('employeeId', employeeId.toString());
    const operations: PatchOperation[] = [{ type: 'REPLACE', attribute: 'trial', value: days }];
    return this.http.patch<void>(
      `${environment.contractUrl}/contracts/${contractId}`,
      operations,
      { params },
    );
  }

  /** Cancel a customer on a given date */
  cancelCustomer(customerId: number, date: string, body: CancelCustomerRequest): Observable<void> {
    return this.http.post<void>(`${environment.bookingMgmtUrl}/customers/${customerId}/cancel`, body, {
      params: { date },
    });
  }

  /** Sync extensions for all contracts of a customer */
  syncExtensions(customerId: number): Observable<void> {
    return this.http
      .put<void>(`${environment.bookingMgmtUrl}/customers/${customerId}/synchronizeextensions`, null)
      // Customer-wide change — we can't map customer→contracts here, so drop the whole cache.
      .pipe(tap(() => this.bookedExtensionsCache.clear()));
  }

  /** Get cancellation reasons for a customer */
  getCancellationReasons(customerId: number): Observable<CancellationReason[]> {
    return this.http.get<CancellationReason[]>(
      `${environment.contractUrl}/customers/${customerId}/cancelationreasons`,
    );
  }
}
